
import { useState, useEffect } from "react";
import axios from "axios";
import "./Acountseeting.css";
import {
  FaChevronDown,
  FaChevronUp,
  FaCheckCircle,
  FaSpinner,
  FaUser,
  FaGraduationCap,
  FaBriefcase,
  FaHeart,
  FaMapMarkerAlt,
  FaVenusMars,
  FaCalendarAlt,
  FaCity,
  FaHome,
  FaLanguage,
  FaUniversity,
  FaFlask,
  FaCog,
  FaGlobe,
  FaSync,
  FaEnvelope,
  FaPhone,
  FaIdCard,
  FaTrashAlt,
  FaTimes,
} from "react-icons/fa";

const SettingsPremium = () => {
  const userId = localStorage.getItem("userId");
  const token = localStorage.getItem("authToken");

  const [openSection, setOpenSection] = useState(null);
  const [savingSection, setSavingSection] = useState(null);
  const [success, setSuccess] = useState("");
  const [globalLoading, setGlobalLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState("");
  const [userData, setUserData] = useState(null);
  const [lastSyncTime, setLastSyncTime] = useState(null);
  const [showLogoutDialog, setShowLogoutDialog] = useState(false);
  const [showMapPopup, setShowMapPopup] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleteChecked, setDeleteChecked] = useState(false);
  
  // Profile details (name, email, mobile)
  const [profileDetails, setProfileDetails] = useState({
    userName: "",
    emailId: "",
    mobileNumber: "",
  });

  // Form data structure matching backend UserAccount schema
  const [formData, setFormData] = useState({
    id: 0,
    userId: userId ? parseInt(userId) : 0,
    gender: "",
    ageGroup: "",
    city: "",
    area: "",
    languagePreference: "",
    educationLevel: "",
    fieldOfStudy: "",
    occupationType: "",
    occupationSubField: "",
    interests: [],
    completionPercentage: 0,
  });

  // ================= LOCAL STORAGE KEYS =================
  const getStorageKey = () => `profileData_user_${userId}`;
  const getSyncTimeKey = () => `profileSyncTime_user_${userId}`;

  // ================= SAVE TO LOCAL STORAGE =================
  const saveToLocalStorage = (data) => {
    if (!userId) return;
    try {
      localStorage.setItem(getStorageKey(), JSON.stringify(data));
      localStorage.setItem(getSyncTimeKey(), new Date().toISOString());
      setLastSyncTime(new Date().toISOString());
    } catch (err) {
      console.error("Failed to save to localStorage:", err);
    }
  };

  // ================= LOAD FROM LOCAL STORAGE =================
  const loadFromLocalStorage = () => {
    if (!userId) return null;
    try {
      const savedData = localStorage.getItem(getStorageKey());
      if (savedData) {
        return JSON.parse(savedData);
      }
    } catch (err) {
      console.error("Failed to load from localStorage:", err);
    }
    return null;
  };

  // ================= FETCH USER PROFILE (name, email, mobile) =================
  const fetchUserProfile = async () => {
    if (!userId) return null;
    try {
      const response = await axios.get(
        `https://backend.maamaas.com/subscription/api/user/getprofile/${userId}`,
        {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        }
      );
      return response.data;
    } catch (err) {
      console.error("Profile fetch error:", err);
      return null;
    }
  };

  // ================= FETCH ACCOUNT DATA FROM API =================
  const fetchAccountFromAPI = async () => {
    if (!userId) return null;
    try {
      const response = await axios.get(
        `https://backend.maamaas.com/subscription/api/user/account/get/${userId}`,
        {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        }
      );
      return response.data;
    } catch (err) {
      console.error("Account fetch error:", err);
      return null;
    }
  };

  // ================= SYNC DATA WITH API =================
  const syncWithAPI = async () => {
    if (!userId) return;

    setSyncing(true);
    try {
      const currentProgress = calculateProgress();
      const payload = {
        userId: parseInt(userId),
        gender: formData.gender || null,
        ageGroup: formData.ageGroup || null,
        city: formData.city || null,
        area: formData.area || null,
        languagePreference: formData.languagePreference || null,
        educationLevel: formData.educationLevel || null,
        fieldOfStudy: formData.fieldOfStudy || null,
        occupationType: formData.occupationType || null,
        occupationSubField: formData.occupationSubField || null,
        interests: formData.interests || [],
        completionPercentage: currentProgress,
      };

      const response = await axios.post(
        "https://backend.maamaas.com/subscription/api/user/account/save",
        payload,
        {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        }
      );

      if (response.data) {
        const updatedData = {
          ...formData,
          completionPercentage: response.data.completionPercentage || currentProgress,
        };
        setFormData(updatedData);
        saveToLocalStorage(updatedData);
      }
      
      setSuccess("Synced with server!");
      setTimeout(() => setSuccess(""), 2000);
    } catch (err) {
      console.error("Sync error:", err);
      setError("Sync failed. Changes saved locally.");
      setTimeout(() => setError(""), 2000);
    } finally {
      setSyncing(false);
    }
  };

  // ================= DELETE ACCOUNT HANDLER =================
  const handleDeleteAccount = async () => {
    if (!userId) {
      setError("No userId found");
      setShowLogoutDialog(false);
      return;
    }
    
    setDeleting(true);
    
    try {
      const response = await fetch(
        `https://backend.maamaas.com/subscription/api/user/delete/account/${userId}`,
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            Authorization: token ? `Bearer ${token}` : "",
          },
        }
      );
      
      if (response.ok) {
        // Clear all localStorage
        localStorage.clear();
        // Show success message
        alert("Account deleted successfully");
        // Redirect to homepage
        window.location.href = "/";
      } else {
        const errMsg = await response.text();
        throw new Error(errMsg || "Failed to delete account");
      }
    } catch (err) {
      setError(err.message);
      setTimeout(() => setError(""), 3000);
    } finally {
      setDeleting(false);
      setShowLogoutDialog(false);
    }
  };

  // ================= INITIAL LOAD =================
  useEffect(() => {
    const initializeData = async () => {
      if (!userId) {
        setGlobalLoading(false);
        setError("User not found. Please login again.");
        return;
      }

      setGlobalLoading(true);
      
      // Load from localStorage
      const localData = loadFromLocalStorage();
      
      // Fetch user profile (name, email, mobile)
      const profileData = await fetchUserProfile();
      if (profileData) {
        setProfileDetails({
          userName: profileData.userName || "",
          emailId: profileData.emailId || "",
          mobileNumber: profileData.mobileNumber || profileData.phoneNumber || "",
        });
      }

      // Fetch account data from API
      const apiData = await fetchAccountFromAPI();
      
      if (apiData) {
        setUserData(apiData);
        
        // Map API response to formData
        const mappedData = {
          id: apiData.id || 0,
          userId: apiData.userId ? parseInt(apiData.userId) : parseInt(userId),
          gender: apiData.gender || "",
          ageGroup: apiData.ageGroup || "",
          city: apiData.city || "",
          area: apiData.area || "",
          languagePreference: apiData.languagePreference || "",
          educationLevel: apiData.educationLevel || "",
          fieldOfStudy: apiData.fieldOfStudy || "",
          occupationType: apiData.occupationType || "",
          occupationSubField: apiData.occupationSubField || "",
          interests: apiData.interests || [],
          completionPercentage: apiData.completionPercentage || 0,
        };
        
        // Use API data if available, otherwise use local data
        if (apiData.gender || apiData.city || apiData.educationLevel) {
          setFormData(mappedData);
          saveToLocalStorage(mappedData);
        } else if (localData) {
          setFormData(localData);
        }
      } else if (localData) {
        setFormData(localData);
      }
      
      setGlobalLoading(false);
    };

    initializeData();
  }, [userId]);

  // ================= AUTO-SAVE TO LOCAL STORAGE ON FORM CHANGE =================
  useEffect(() => {
    if (!globalLoading && userId) {
      saveToLocalStorage(formData);
    }
  }, [formData, globalLoading, userId]);

  // ================= TOGGLE =================
  const toggleSection = (section) => {
    setOpenSection(openSection === section ? null : section);
  };

  // ================= CHANGE =================
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // ================= INTEREST =================
  const handleInterest = (val) => {
    let updated = [...(formData.interests || [])];
    if (updated.includes(val)) {
      updated = updated.filter((i) => i !== val);
    } else {
      updated.push(val);
    }
    setFormData((prev) => ({ ...prev, interests: updated }));
  };

  // ================= PROGRESS =================
  const calculateProgress = () => {
    let total = 0;
    if (formData.gender && formData.gender !== "") total += 12.5;
    if (formData.ageGroup && formData.ageGroup !== "") total += 12.5;
    if (formData.city && formData.city !== "") total += 12.5;
    if (formData.area && formData.area !== "") total += 12.5;
    if (formData.educationLevel && formData.educationLevel !== "") total += 12.5;
    if (formData.fieldOfStudy && formData.fieldOfStudy !== "") total += 12.5;
    if (formData.occupationType && formData.occupationType !== "") total += 12.5;
    if (formData.occupationSubField && formData.occupationSubField !== "") total += 12.5;
    if (formData.interests && formData.interests.length > 0) total += 12.5;
    return Math.min(total, 100);
  };

  // ================= SAVE SPECIFIC SECTION =================
  const handleSaveSection = async (sectionId) => {
    if (!userId) {
      setError("User not authenticated");
      return;
    }

    setSavingSection(sectionId);
    setError("");
    setSuccess("");

    const currentProgress = calculateProgress();
    
    const updatedFormData = {
      ...formData,
      completionPercentage: currentProgress,
    };
    setFormData(updatedFormData);
    saveToLocalStorage(updatedFormData);

    const payload = {
      userId: parseInt(userId),
      gender: updatedFormData.gender || null,
      ageGroup: updatedFormData.ageGroup || null,
      city: updatedFormData.city || null,
      area: updatedFormData.area || null,
      languagePreference: updatedFormData.languagePreference || null,
      educationLevel: updatedFormData.educationLevel || null,
      fieldOfStudy: updatedFormData.fieldOfStudy || null,
      occupationType: updatedFormData.occupationType || null,
      occupationSubField: updatedFormData.occupationSubField || null,
      interests: updatedFormData.interests || [],
      completionPercentage: currentProgress,
    };

    try {
      const response = await axios.post(
        "https://backend.maamaas.com/subscription/api/user/account/save",
        payload,
        {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        }
      );

      if (response.data) {
        const syncedData = {
          ...updatedFormData,
          completionPercentage: response.data.completionPercentage || currentProgress,
        };
        setFormData(syncedData);
        saveToLocalStorage(syncedData);
      }

      setSuccess(`${getSectionName(sectionId)} saved successfully!`);
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      console.error("Save error:", err);
      setError("Saved locally but server sync failed. Will retry later.");
      setTimeout(() => setError(""), 3000);
    } finally {
      setSavingSection(null);
    }
  };

  const getSectionName = (sectionId) => {
    const names = {
      basic: "Basic information",
      personal: "Personal information",
      education: "Education details",
      occupation: "Occupation details",
      interest: "Interests",
      preferences: "Preferences",
    };
    return names[sectionId] || "Settings";
  };

  // ================= SECTION CONFIGURATION =================
  const sections = [
    {
      id: "basic",
      title: "Basic Information",
      description: "Your name, email and contact details",
      icon: <FaIdCard />,
      color: "#3498db",
      fields: (
        <>
          <div className="profileSetup-infoGrid">
            <div className="profileSetup-infoCard">
              <div className="profileSetup-infoIcon">
                <FaUser />
              </div>
              <div className="profileSetup-infoContent">
                <label className="profileSetup-infoLabel">Full Name</label>
                <input
                  type="text"
                  className="profileSetup-input"
                  value={profileDetails.userName || ""}
                  disabled
                  style={{ backgroundColor: "#f5f5f5", cursor: "not-allowed" }}
                />
              </div>
            </div>
            <div className="profileSetup-infoCard">
              <div className="profileSetup-infoIcon">
                <FaEnvelope />
              </div>
              <div className="profileSetup-infoContent">
                <label className="profileSetup-infoLabel">Email Address</label>
                <input
                  type="email"
                  className="profileSetup-input"
                  value={profileDetails.emailId || ""}
                  disabled
                  style={{ backgroundColor: "#f5f5f5", cursor: "not-allowed" }}
                />
              </div>
            </div>
            <div className="profileSetup-infoCard">
              <div className="profileSetup-infoIcon">
                <FaPhone />
              </div>
              <div className="profileSetup-infoContent">
                <label className="profileSetup-infoLabel">Mobile Number</label>
                <input
                  type="tel"
                  className="profileSetup-input"
                  value={profileDetails.mobileNumber || ""}
                  disabled
                  style={{ backgroundColor: "#f5f5f5", cursor: "not-allowed" }}
                />
              </div>
            </div>
          </div>
        </>
      ),
    },
    {
      id: "personal",
      title: "Personal Information",
      description: "Gender, age and location details",
      icon: <FaUser />,
      color: "#e74c3c",
      fields: (
        <>
          <div className="profileSetup-infoGrid">
            <div className="profileSetup-infoCard">
              <div className="profileSetup-infoIcon">
                <FaVenusMars />
              </div>
              <div className="profileSetup-infoContent">
                <label className="profileSetup-infoLabel">Gender</label>
                <select
                  name="gender"
                  className="profileSetup-select"
                  value={formData.gender || ""}
                  onChange={handleChange}
                >
                  <option value="">Select Gender</option>
                  <option value="MALE">Male</option>
                  <option value="FEMALE">Female</option>
                </select>
              </div>
            </div>
            <div className="profileSetup-infoCard">
              <div className="profileSetup-infoIcon">
                <FaCalendarAlt />
              </div>
              <div className="profileSetup-infoContent">
                <label className="profileSetup-infoLabel">Age Group</label>
                <select
                  name="ageGroup"
                  className="profileSetup-select"
                  value={formData.ageGroup || ""}
                  onChange={handleChange}
                >
                  <option value="">Select Age Group</option>
                  <option value="BELOW_18">Below 18</option>
                  <option value="AGE_18_25">18-25 years</option>
                  <option value="AGE_26_35">26-35 years</option>
                  <option value="AGE_36_45">36-45 years</option>
                  <option value="AGE_45_PLUS">45+ years</option>
                </select>
              </div>
            </div>
            <div className="profileSetup-infoCard">
              <div className="profileSetup-infoIcon">
                <FaCity />
              </div>
              <div className="profileSetup-infoContent">
                <label className="profileSetup-infoLabel">City / District</label>
                <input
                  type="text"
                  name="city"
                  className="profileSetup-input"
                  placeholder="e.g., New York, London, Mumbai"
                  value={formData.city || ""}
                  onChange={handleChange}
                />
              </div>
            </div>
            <div className="profileSetup-infoCard">
              <div className="profileSetup-infoIcon">
                <FaHome />
              </div>
              <div className="profileSetup-infoContent">
                <label className="profileSetup-infoLabel">Area / Locality</label>
                <input
                  type="text"
                  name="area"
                  className="profileSetup-input"
                  placeholder="e.g., Downtown, Suburb, Sector 5"
                  value={formData.area || ""}
                  onChange={handleChange}
                />
              </div>
            </div>
          </div>
        </>
      ),
    },
    {
      id: "education",
      title: "Education",
      description: "Your academic background",
      icon: <FaGraduationCap />,
      color: "#2ecc71",
      fields: (
        <>
          <div className="profileSetup-infoGrid">
            <div className="profileSetup-infoCard">
              <div className="profileSetup-infoIcon">
                <FaUniversity />
              </div>
              <div className="profileSetup-infoContent">
                <label className="profileSetup-infoLabel">Education Level</label>
                <select
                  name="educationLevel"
                  className="profileSetup-select"
                  value={formData.educationLevel || ""}
                  onChange={handleChange}
                >
                  <option value="">Select Level</option>
                  <option value="SCHOOL">School</option>
                  <option value="INTERMEDIATE_DIPLOMA">Intermediate / Diploma</option>
                  <option value="GRADUATE">Graduate</option>
                  <option value="POST_GRADUATE">Post Graduate</option>
                </select>
              </div>
            </div>
            <div className="profileSetup-infoCard">
              <div className="profileSetup-infoIcon">
                <FaFlask />
              </div>
              <div className="profileSetup-infoContent">
                <label className="profileSetup-infoLabel">Field of Study</label>
                <select
                  name="fieldOfStudy"
                  className="profileSetup-select"
                  value={formData.fieldOfStudy || ""}
                  onChange={handleChange}
                >
                  <option value="">Select Field</option>
                  <option value="SCIENCE">Science</option>
                  <option value="COMMERCE">Commerce</option>
                  <option value="ARTS">Arts</option>
                  <option value="ENGINEERING">Engineering</option>
                  <option value="IT">Information Technology</option>
                  <option value="MANAGEMENT">Management</option>
                  <option value="MEDICAL">Medical</option>
                  <option value="OTHER">Other</option>
                </select>
              </div>
            </div>
          </div>
        </>
      ),
    },
    {
      id: "occupation",
      title: "Occupation",
      description: "Your professional status",
      icon: <FaBriefcase />,
      color: "#f39c12",
      fields: (
        <>
          <div className="profileSetup-infoGrid">
            <div className="profileSetup-infoCard">
              <div className="profileSetup-infoIcon">
                <FaBriefcase />
              </div>
              <div className="profileSetup-infoContent">
                <label className="profileSetup-infoLabel">Occupation Type</label>
                <select
                  name="occupationType"
                  className="profileSetup-select"
                  value={formData.occupationType || ""}
                  onChange={handleChange}
                >
                  <option value="">Select Type</option>
                  <option value="STUDENT">Student</option>
                  <option value="EMPLOYED">Employed</option>
                  <option value="FREELANCER">Freelancer</option>
                  <option value="ENTREPRENEUR">Entrepreneur</option>
                  <option value="HOMEMAKER">Homemaker</option>
                  <option value="JOB_SEEKER">Job Seeker</option>
                </select>
              </div>
            </div>
            <div className="profileSetup-infoCard">
              <div className="profileSetup-infoIcon">
                <FaCog />
              </div>
              <div className="profileSetup-infoContent">
                <label className="profileSetup-infoLabel">Sub Category / Role</label>
                <input
                  type="text"
                  name="occupationSubField"
                  className="profileSetup-input"
                  placeholder="e.g., Frontend Developer, Marketing Manager"
                  value={formData.occupationSubField || ""}
                  onChange={handleChange}
                />
              </div>
            </div>
          </div>
        </>
      ),
    },
    {
      id: "interest",
      title: "Interests & Hobbies",
      description: "Select topics you enjoy",
      icon: <FaHeart />,
      color: "#e91e63",
      fields: (
        <>
          <div className="profileSetup-interestSection">
            <div className="profileSetup-interestGrid">
              {[
                "JOBS", "FOOD", "EDUCATION", "OFFERS", "REAL_ESTATE",
                "ONLINE_COURSES", "BAKERY", "HEALTH", "TRAVEL", "ENTERTAINMENT"
              ].map((i) => (
                <button
                  key={i}
                  type="button"
                  className={`profileSetup-interestBtn ${
                    formData.interests?.includes(i) ? "profileSetup-interestBtn--active" : ""
                  }`}
                  onClick={() => handleInterest(i)}
                >
                  {i.replace(/_/g, " ")}
                </button>
              ))}
            </div>
            <p className="profileSetup-interestHint">
              Selected: {formData.interests?.length || 0} interests
            </p>
          </div>
        </>
      ),
    },
    {
      id: "preferences",
      title: "Preferences",
      description: "Language and app settings",
      icon: <FaLanguage />,
      color: "#9b59b6",
      fields: (
        <>
          <div className="profileSetup-infoGrid">
            <div className="profileSetup-infoCard">
              <div className="profileSetup-infoIcon">
                <FaGlobe />
              </div>
              <div className="profileSetup-infoContent">
                <label className="profileSetup-infoLabel">Language Preference</label>
                <select
                  name="languagePreference"
                  className="profileSetup-select"
                  value={formData.languagePreference || "English"}
                  onChange={handleChange}
                >
                  <option value="English">English</option>
                  <option value="Hindi">Hindi</option>
                  <option value="Spanish">Spanish</option>
                  <option value="French">French</option>
                  <option value="Arabic">Arabic</option>
                </select>
              </div>
            </div>
          </div>
        </>
      ),
    },
  ];

  // Loading state
  if (globalLoading) {
    return (
      <div className="profileSetup-root">
        <div className="profileSetup-container">
          <div className="profileSetup-loading">
            <FaSpinner className="profileSetup-spinner" />
            <p>Loading your profile...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="profileSetup-root">
      <div className="profileSetup-container">
        {/* Header */}
        {/* <div className="profileSetup-header">
          <div className="profileSetup-headerContent">
            <h1 className="profileSetup-title">Account Settings</h1>
            <p className="profileSetup-subtitle">
              Manage your profile information and preferences
            </p>
          </div>
        </div> */}

        {/* Messages */}
        {success && (
          <div className="profileSetup-successMsg">
            <FaCheckCircle />
            <span>{success}</span>
          </div>
        )}
        {error && (
          <div className="profileSetup-errorMsg">
            <FaSpinner />
            <span>{error}</span>
          </div>
        )}

        {/* Progress Section */}
        {/* <div className="profileSetup-progressCard">
          <div className="profileSetup-progressHeader">
            <span className="profileSetup-progressLabel">Profile Completion</span>
            <span className="profileSetup-progressPercent">{calculateProgress()}%</span>
          </div>
          <div className="profileSetup-progressBar">
            <div
              className="profileSetup-progressFill"
              style={{ width: `${calculateProgress()}%` }}
            />
          </div>
          <p className="profileSetup-progressHint">
            Complete more sections to improve your profile
          </p>
        </div> */}

        {/* Accordion Sections */}
        <div className="profileSetup-sections">
          {sections.map((section) => (
            <div
              key={section.id}
              className={`profileSetup-section ${
                openSection === section.id ? "profileSetup-section--open" : ""
              }`}
            >
              <button
                className="profileSetup-dropdownBtn"
                onClick={() => toggleSection(section.id)}
                style={{ borderLeftColor: section.color }}
              >
                <div className="profileSetup-btnContent">
                  <div className="profileSetup-btnLeft">
                    <div
                      className="profileSetup-btnIcon"
                      style={{ backgroundColor: `${section.color}15`, color: section.color }}
                    >
                      {section.icon}
                    </div>
                    <div className="profileSetup-btnText">
                      <h3 className="profileSetup-btnTitle">{section.title}</h3>
                      <p className="profileSetup-btnDesc">{section.description}</p>
                    </div>
                  </div>
                  <div className="profileSetup-btnArrow">
                    {openSection === section.id ? <FaChevronUp /> : <FaChevronDown />}
                  </div>
                </div>
              </button>

              {openSection === section.id && (
                <div className="profileSetup-dropdownContent">
                  {section.fields}
                  <button
                    className="profileSetup-saveBtn"
                    onClick={() => handleSaveSection(section.id)}
                    disabled={savingSection === section.id}
                  >
                    {savingSection === section.id ? <FaSpinner className="profileSetup-spinnerIcon" /> : <FaCheckCircle />}
                    {savingSection === section.id ? "Saving..." : "Save Changes"}
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Footer with Delete Account Button */}
        {/* FOOTER */}
{/* DELETE SECTION */}
<div className="profileSetup-footer">
  <div className="profileSetup-footerContent">

    {/* LEFT TEXT */}
    <div className="profileSetup-footerLeft">
      
      {/* CHECKBOX */}
      {/* CHECKBOX */}
<label className="profileSetup-checkboxLabel">
  <input
    type="checkbox"
    checked={deleteChecked}
    onChange={(e) => {
      setDeleteChecked(e.target.checked);

      if (e.target.checked) {
        setShowLogoutDialog(true);
      }
    }}
  />

  <span>
    I understand and want to delete my account
  </span>
</label>
    </div>
  </div>
</div>

{/* DELETE POPUP */}
{showLogoutDialog && (
  <div className="delete-confirm-overlay">

    <div className="delete-confirm-dialog">

      {/* CLOSE */}
      {/* CLOSE BUTTON */}
<button
  className="delete-confirm-close"
  onClick={() => {
    setShowLogoutDialog(false);
    setDeleteChecked(false); // 👈 checkbox uncheck
  }}
>
  <FaTimes />
</button>

      {/* ICON */}
      <div className="delete-confirm-icon">
        <FaTrashAlt />
      </div>

      <h3>Delete Account</h3>

      <p>
        Are you sure you want to permanently delete your account?
      </p>

      <p className="warning-text">
        ⚠️ This action cannot be undone.
      </p>

      {/* BUTTONS */}
      <div className="dialog-buttons">

        {/* CANCEL BUTTON */}
<button
  className="cancel-btn"
  onClick={() => {
    setShowLogoutDialog(false);
    setDeleteChecked(false); // 👈 checkbox uncheck
  }}
  disabled={deleting}
>
  Cancel
</button>

        <button
          className="delete-btn"
          onClick={handleDeleteAccount}
          disabled={deleting}
        >
          {deleting ? (
            <FaSpinner className="spinner" />
          ) : (
            <FaTrashAlt />
          )}

          {deleting ? "Deleting..." : "Delete Account"}
        </button>

      </div>
    </div>
  </div>
)}
      </div>
    </div>
  );
}

export default SettingsPremium;